import {
	FilterGroup,
	SearchFilter,
	SearchFilterState,
	SearchSortValue
} from './../redux/search/search.types';
import { MapSearchFilter, MapSearchFilterGroup } from './../models/models';
import { config } from './../config/Config';
import { get, remove } from './HttpService';
import {
	MapSearchDto,
	MapSearchResult as MapSearchResultDto,
	MapSearchSuggestionDto,
	MapSearchSuggestionListDto
} from '../models/models';
import {
	MapSearchResult,
	MapSearchSuggestion,
	SearchResultState,
	SearchSuggestions
} from '../redux/search/search.types';
import { uniq } from 'ramda';
import { HttpResponse } from '../redux/http/http.types';
import { isSuccessCode } from '../utils';
import { dispatchSnackbar } from './SnackbarService';
import i18next from '../i18n/i18nInit';

let abortController: AbortController | undefined;

export async function searchMap(
	searchValue: string,
	page: number,
	pageSize: number,
	filterState?: SearchFilterState,
	sortValue?: SearchSortValue
): Promise<SearchResultState | undefined> {
	if (abortController) {
		abortController.abort();
		abortController = undefined;
	}
	abortController = new AbortController();
	const searchQuery: string = createSearchQueryString(
		searchValue,
		page,
		pageSize,
		filterState,
		sortValue
	);
	const response: HttpResponse<MapSearchResultDto> =
		await get<MapSearchResultDto>(
			'search',
			`maps/search${searchQuery}`,
			abortController
		);

	const results: MapSearchResultDto = response.data;
	return results ? mapSearchResultDtoToObject(results) : undefined;
}

export function isValidSearchValue(value: string): boolean {
	return (
		value.length >= config.minCharactersForSearch ||
		value.startsWithNumber()
	);
}

function createSearchQueryString(
	searchValue: string,
	page: number,
	pageSize: number,
	filterState?: SearchFilterState,
	sort?: SearchSortValue
): string {
	let queryString: string = `?searchvalue=${searchValue}&pageindex=${page}&pagesize=${pageSize}`;

	if (!filterState || filterState === {}) {
		return sort ? (queryString += getSortString(sort)) : queryString;
	}

	const regionList: string = arrayToString(filterState['Regions']);
	const categoryList: string = arrayToString(filterState['Categories']);
	const themeList: string = arrayToString(filterState['Themes']);
	const typeList: string = arrayToString(filterState['Type']);

	queryString += regionList.length > 0 ? `&regions=${regionList}` : '';
	queryString += categoryList.length > 0 ? `&categories=${categoryList}` : '';
	queryString += themeList.length > 0 ? `&themes=${themeList}` : '';
	queryString += typeList.length > 0 ? `&types=${typeList}` : '';

	return sort ? (queryString += getSortString(sort)) : queryString;
}

function getSortString(sort: SearchSortValue): string {
	switch (sort) {
		case 'mapNumber':
			return '&sortProperty=PageNumber';
		case 'ascending':
			return '&sortProperty=Title';
		case 'descending':
			return '&sortProperty=Title&isDescending=true';
	}
}

export async function getSuggestions(
	searchValue: string
): Promise<SearchSuggestions> {
	const searchQuery: string = `?searchvalue=${searchValue}`;
	const response: HttpResponse<MapSearchSuggestionListDto> =
		await get<MapSearchSuggestionListDto>(
			'search-suggestions',
			`maps/suggestions${searchQuery}`
		);
	const results: MapSearchSuggestionListDto = response.data!;

	return suggestionListDtoToObject(results);
}

export function addSearchToHistory(value: string): void {
	if (value.trim().length === 0) return;

	const history: string[] = getSearchHistory();
	if (history.length === config.searchHistoryMaxItems) {
		history.shift();
	}
	history.push(value);
	const historyString: string = JSON.stringify(history);
	localStorage.setItem(config.searchHistoryLocalstorageKey, historyString);
}

export function getSearchHistory(): string[] {
	const historyString: string | null = localStorage.getItem(
		config.searchHistoryLocalstorageKey
	);
	if (!historyString) return [];
	return uniq(JSON.parse(historyString));
}

export async function clearSearchCache(): Promise<void> {
	const response: HttpResponse = await remove('maps', 'maps/cache');

	const success: boolean = isSuccessCode(response);
	const message: string = success
		? i18next.t('contentManager:searchCacheDeleted')
		: i18next.t('contentManager:searchCacheNotDeleted');

	dispatchSnackbar(`map`, `DELETE`, success, message);
}

function mapSearchResultDtoToObject(
	dto: MapSearchResultDto
): SearchResultState {
	return {
		page: dto.page,
		pageSize: dto.pageSize,
		totalPages: dto.totalPages,
		count: dto.count,
		items: dto.items!.map(mapSearchDtoToObject),
		filterGroups: dto.filterGroups!.map(filterGroupDtoToObject)
	};
}

function mapSearchDtoToObject(result: MapSearchDto): MapSearchResult {
	return {
		id: result.id,
		title: result.title as string,
		aliasses: result.aliasses as string[],
		category: result.category as string,
		theme: result.theme as string,
		chapter: result.chapter as string,
		region: result.region as string,
		pageNumber: result.pageNumber,
		pageSection: result.pageSection as string,
		isDigitalOnly: result.isDigitalOnly,
		thumbnailUrl: result.thumbnailUrl as string,
		topoLat: result.topoLat
			? Number(result.topoLat.replace(',', '.'))
			: undefined,
		topoLng: result.topoLng
			? Number(result.topoLng.replace(',', '.'))
			: undefined,
		topoName: result.topoName,
		topoSuffix: result.topoSuffix
	};
}

function filterGroupDtoToObject(dto: MapSearchFilterGroup): FilterGroup {
	return {
		name: dto.name as string,
		filters: dto.filters!.map(filterDtoToObject)
	};
}

function filterDtoToObject(dto: MapSearchFilter): SearchFilter {
	return {
		name: dto.name as string,
		count: dto.resultCount
	};
}

function suggestionListDtoToObject(
	resultList: MapSearchSuggestionListDto
): SearchSuggestions {
	const resultPreviews: MapSearchResult[] = (
		resultList.resultPreview as MapSearchDto[]
	).map(mapSearchDtoToObject);

	const suggestions: MapSearchSuggestion[] = (
		resultList.suggestions as MapSearchSuggestionDto[]
	).map(suggestionDtoToObject);

	return {
		suggestions,
		resultPreviews
	};
}

function suggestionDtoToObject(
	suggestion: MapSearchSuggestionDto
): MapSearchSuggestion {
	return {
		suggestion: suggestion.suggestion as string,
		count: suggestion.count
	};
}

function arrayToString(array: string[]): string {
	if (!array || array.length === 0) return '';
	return array.join(',');
}
